import type { LucideIcon } from 'lucide-react'
import {
  BookTemplate,
  Clapperboard,
  Download,
  Film,
  FolderKanban,
  Layers3,
  MessageSquareQuote,
  Sparkles,
  Wand2,
  Workflow,
  ShieldCheck,
  PanelTop,
  Palette,
  Users,
} from 'lucide-react'

export interface HomeCardItem {
  title: string
  desc: string
  icon: LucideIcon
  tag?: string
}

export interface PipelineItem {
  step: string
  title: string
  desc: string
  icon: LucideIcon
}

export interface SpecItem {
  title: string
  desc: string
  value: string
  icon: LucideIcon
}

export const valueProps: HomeCardItem[] = [
  {
    title: '零门槛上手',
    desc: '无需绘画基础和剪辑经验，输入故事灵感即可开始创作完整漫剧。',
    icon: Sparkles,
  },
  {
    title: '全流程覆盖',
    desc: '从剧本、元素、分镜到配音、合成，一套系统串起整个生产链路。',
    icon: Workflow,
  },
  {
    title: '高效产出',
    desc: '支持多画幅项目并行、批量生成与一键导出，快速验证内容方向。',
    icon: Clapperboard,
  },
]

export const featureModules: HomeCardItem[] = [
  {
    title: '项目管理',
    desc: '支持 6 种画幅、全局 AI 风格、封面生成与共享示例项目，统一管理创作资产。',
    icon: FolderKanban,
    tag: '6 种画幅',
  },
  {
    title: '剧本与剧集',
    desc: '支持 AI 写剧本、上传剧本、多集内容组织与右侧剧本编辑器协同创作。',
    icon: BookTemplate,
    tag: 'AI 剧本',
  },
  {
    title: '智能剧本分析',
    desc: '自动提取角色、场景、道具并生成分镜初稿，让文字快速进入画面阶段。',
    icon: Wand2,
    tag: '一键分析',
  },
  {
    title: '元素管理',
    desc: '角色、场景、道具统一管理，支持单张与批量出图，并可为角色绑定音色。',
    icon: Layers3,
    tag: '批量生成',
  },
  {
    title: '分镜编辑',
    desc: '支持分镜图、分镜视频、图生图、提示词推断与元素融合，逐镜头精修作品。',
    icon: PanelTop,
    tag: '分镜图/视频',
  },
  {
    title: 'AI 配音',
    desc: '可按剧集与分镜同步对白、选择音色并批量生成配音，适合角色化表达。',
    icon: MessageSquareQuote,
    tag: '需开启语音',
  },
  {
    title: '视频合成',
    desc: '时间线自动填充视频与音频轨道，统一调整画幅、分辨率和适配方式后提交生成。',
    icon: Film,
    tag: '时间线编辑',
  },
  {
    title: '提示词模板',
    desc: '剧本、元素、分镜、视频等环节可沉淀模板，提高团队复用效率与风格一致性。',
    icon: Sparkles,
    tag: '模板复用',
  },
  {
    title: '项目导出',
    desc: '素材、分镜、配音、视频支持统一打包导出，便于交付、复盘与二次分发。',
    icon: Download,
    tag: '一键导出',
  },
]

export const pipelineItems: PipelineItem[] = [
  {
    step: '01',
    title: '创建项目',
    desc: '先确定画幅、风格与项目主题，把后续所有素材、剧集和分镜集中到同一工作台。',
    icon: FolderKanban,
  },
  {
    step: '02',
    title: '生成剧本与剧集',
    desc: '用 AI 创作剧本，或直接上传已有剧本，再拆分为多集内容进行持续创作。',
    icon: BookTemplate,
  },
  {
    step: '03',
    title: 'AI 分析剧本',
    desc: '自动识别角色、场景、道具和分镜结构，减少前期拆解与人工整理成本。',
    icon: Wand2,
  },
  {
    step: '04',
    title: '制作角色与场景元素',
    desc: '对核心角色、场景和道具进行单张或批量出图，保证后续镜头风格稳定。',
    icon: Layers3,
  },
  {
    step: '05',
    title: '生成并编辑分镜',
    desc: '逐镜头完善画面描述、对白和视觉效果，并继续生成分镜图或视频素材。',
    icon: PanelTop,
  },
  {
    step: '06',
    title: '生成配音',
    desc: '按分镜同步对白与音色，统一生成角色台词，为成片增加节奏和情绪表达。',
    icon: MessageSquareQuote,
  },
  {
    step: '07',
    title: '合成与导出',
    desc: '在时间线上统一编排镜头、音频和参数，提交生成后直接导出完整项目成果。',
    icon: Clapperboard,
  },
]

export const platformSpecs: SpecItem[] = [
  {
    title: '剧本一键拆解',
    desc: '上传或 AI 生成剧本后，自动提取角色、场景、道具并生成分镜结构，让创作从文字阶段快速进入画面阶段。',
    value: '文字直达分镜',
    icon: Wand2,
  },
  {
    title: '角色形象锁定',
    desc: '主角与配角在不同分镜中保持形象稳定，减少脸崩、风格漂移和反复返工，提升整集观感连贯性。',
    value: '跨镜头一致',
    icon: Users,
  },
  {
    title: '全局 AI 风格',
    desc: '项目级统一画风设定，贯穿元素出图、分镜生成与视频制作，让整部作品视觉语言保持一致。',
    value: '整片风格统一',
    icon: Palette,
  },
  {
    title: 'OEM 白标能力',
    desc: '支持独立域名、Logo、支付与客服配置，适合品牌化运营。',
    value: '品牌与支付可定制',
    icon: ShieldCheck,
  },
]
